import { Button, ScrollArea, Stack } from '@mantine/core';
import { useEffect } from 'react';

import { Transit, TransitDocumentsEnum, TransitGroup, TransitStatus } from '@e-origin/shared';

import { LeavePageNotification, StickyContainer } from '@components';
import { TransitDocuments } from './TransitDocuments';
import { useTransitViewDocumentsFormInitializer } from './useTransitViewDocumentsFormInitializer';

interface TransitViewDocumentsProps {
  transit: Transit;
  editDisabled: boolean;
  editDisabledConditionalForArrival: boolean;
}

export interface DocumentItemProps {
  type: TransitDocumentsEnum;
  label?: string;
  code: string;
  referenceNumber?: string;
  dateOfValidity?: string;
  dataSource: any;
  dataSourceIndex: number;
}

export const TransitViewDocuments: React.FC<TransitViewDocumentsProps> = ({ transit, editDisabled, editDisabledConditionalForArrival }) => {
  const { form, onSubmit, buildFormValues } = useTransitViewDocumentsFormInitializer({ transit });
  const isArrival = transit.generalInfo.group === TransitGroup.ARRIVAL;
  const isUnloadPermission = isArrival && transit.generalInfo.status === TransitStatus.UNLOAD_PERMISSION;

  const handleDocumentDelete = (documentType: any, documentIndex: number) => {
    form.setFieldValue(`documents.${documentType}`, [...(form.values.documents[documentType] || []).filter((_, idx) => idx !== documentIndex)]);
  };

  const handleSavingDocument = (savedDocument) => {
    if (savedDocument.docIndex >= 0) {
      const updatedDocuments = form.values.documents[savedDocument.type].map((doc, idx) => {
        if (idx === savedDocument.docIndex) {
          return savedDocument.data;
        }
        return doc;
      });
      form.setFieldValue(`documents.${savedDocument.type}`, updatedDocuments);
    } else {
      form.setFieldValue(`documents.${savedDocument.type}`, [...(form.values.documents[savedDocument.type] || []), savedDocument.data]);
    }
  };

  useEffect(() => {
    form.setValues(buildFormValues(transit));
    form.resetDirty({});
  }, [transit]);

  return (
    <>
      <LeavePageNotification hasUnsavedChanges={form.isDirty()} />

      <ScrollArea h="100%" offsetScrollbars="y">
        <form onSubmit={form.onSubmit(onSubmit)}>
          <Stack mx="xl">
            <TransitDocuments
              isArrival={isArrival}
              documents={form.values.documents}
              onDelete={handleDocumentDelete}
              onSave={handleSavingDocument}
              disabledDocuments={isArrival ? [TransitDocumentsEnum.PREVIOUS_DOCUMENT, TransitDocumentsEnum.ADDITIONAL_INFORMATION] : []}
              disabled={isArrival ? editDisabledConditionalForArrival : editDisabled}
            />
          </Stack>

          <StickyContainer>
            <Button
              type="submit"
              disabled={(isArrival ? editDisabledConditionalForArrival : editDisabled) || !form.isValid()}
              variant="filled"
              size="lg"
              data-pw="save-transit-btn"
            >
              Save
            </Button>
          </StickyContainer>
        </form>
      </ScrollArea>
    </>
  );
};
